import { byId, isAr, formatCurrency, formatNumber, attachCopyButton, setupLiveCalculation, detectUserCountry, detectUserCurrency } from './calc-common.js';
import Big from 'big.js';
import { getInvoiceTafgeet } from './invoice-currencies.js';

export const ARAB_FUEL_PRESETS = {
  SA: { price: 2.18, currency: 'SAR' },
  AE: { price: 3.05, currency: 'AED' },
  EG: { price: 15.25, currency: 'EGP' },
  KW: { price: 0.085, currency: 'KWD' },
  QA: { price: 1.95, currency: 'QAR' },
  OM: { price: 0.229, currency: 'OMR' },
  BH: { price: 0.140, currency: 'BHD' },
  JO: { price: 0.910, currency: 'JOD' },
  MA: { price: 14.50, currency: 'MAD' },
  DZ: { price: 45.00, currency: 'DZD' },
  TN: { price: 2.525, currency: 'TND' },
  IQ: { price: 650, currency: 'IQD' }
};

export function calculateFuelTrip({
  distance,
  isRoundTrip = false,
  efficiencyUnit = 'kml', // 'kml' | 'l100km' | 'mpg'
  efficiencyValue,
  fuelPrice,
  passengers = 1
}) {
  const dist = isRoundTrip ? Big(distance).times(2) : Big(distance);
  let litersNeeded = Big(0);

  if (efficiencyUnit === 'kml') {
    // km per liter
    litersNeeded = Big(efficiencyValue).gt(0) ? dist.div(Big(efficiencyValue)) : Big(0);
  } else if (efficiencyUnit === 'l100km') {
    // liters per 100 km
    litersNeeded = dist.times(Big(efficiencyValue)).div(100);
  } else if (efficiencyUnit === 'mpg') {
    // US MPG: 1 MPG = 0.425143707 km/L
    const kmlEquivalent = Big(efficiencyValue).times('0.425143707');
    litersNeeded = kmlEquivalent.gt(0) ? dist.div(kmlEquivalent) : Big(0);
  }

  const totalCost = litersNeeded.times(Big(fuelPrice));
  const pax = Math.max(1, passengers);
  const costPerPerson = totalCost.div(pax);

  const costPer100km = dist.gt(0) ? totalCost.times(100).div(dist) : Big(0);

  return {
    totalDistance: Number(dist.toFixed(2)),
    litersNeeded: Number(litersNeeded.toFixed(2)),
    totalCost: Number(totalCost.toFixed(2)),
    costPerPerson: Number(costPerPerson.toFixed(2)),
    costPer100km: Number(costPer100km.toFixed(2))
  };
}

export function initFuelCostCalculator() {
  const container = byId('fuel-cost-calculator-app');
  if (!container) return;

  // Auto-detect visitor's country and default fuel price (Saudi fallback)
  const detected = (detectUserCountry() || 'SA').toUpperCase();
  const fuelPreset = ARAB_FUEL_PRESETS[detected] || ARAB_FUEL_PRESETS.SA;
  const priceInput = byId('fuel-price');
  const curSelect = byId('fuel-currency');
  if (priceInput) priceInput.value = fuelPreset.price;
  if (curSelect) curSelect.value = fuelPreset.currency;

  setupLiveCalculation([
    'fuel-distance',
    'fuel-roundtrip',
    'fuel-efficiency-val',
    'fuel-efficiency-unit',
    'fuel-price',
    'fuel-passengers',
    'fuel-currency'
  ], runFuelCalc);

  // Fuel price presets
  document.querySelectorAll('.fuel-preset-chip').forEach((chip) => {
    chip.addEventListener('click', () => {
      const price = chip.dataset.price;
      const cur = chip.dataset.currency;
      const priceInput = byId('fuel-price');
      const curSelect = byId('fuel-currency');
      if (priceInput && price) priceInput.value = price;
      if (curSelect && cur) curSelect.value = cur;
      runFuelCalc();
    });
  });

  attachCopyButton(byId('fuel-copy-btn'), () => {
    const totalCost = byId('fuel-total-cost')?.textContent || '';
    const perPax = byId('fuel-per-person')?.textContent || '';
    const liters = byId('fuel-total-liters')?.textContent || '';
    const dist = byId('fuel-total-dist')?.textContent || '';
    return isAr
      ? `📋 تقدير تكلفة وقود الرحلة:\n• إجمالي تكلفة الوقود: ${totalCost}\n• المسافة الكلية: ${dist}\n• كمية الوقود اللازمة: ${liters}\n• نصيب الراكب الواحد: ${perPax}`
      : `📋 Fuel Trip Cost Estimate:\n• Total Fuel Cost: ${totalCost}\n• Total Distance: ${dist}\n• Fuel Required: ${liters}\n• Share Per Passenger: ${perPax}`;
  });

  const printBtn = byId('fuel-print-btn');
  if (printBtn) {
    printBtn.addEventListener('click', () => {
      window.print();
    });
  }

  runFuelCalc();
}

function runFuelCalc() {
  const distance = parseFloat(byId('fuel-distance')?.value) || 0;
  const isRoundTrip = byId('fuel-roundtrip')?.checked || false;
  const efficiencyUnit = byId('fuel-efficiency-unit')?.value || 'kml';
  const efficiencyValue = parseFloat(byId('fuel-efficiency-val')?.value) || 12;
  const fuelPrice = parseFloat(byId('fuel-price')?.value) || 2.18;
  const passengers = parseInt(byId('fuel-passengers')?.value, 10) || 1;
  const currency = byId('fuel-currency')?.value || 'SAR';

  if (distance <= 0 || efficiencyValue <= 0 || fuelPrice <= 0) {
    if (byId('fuel-total-cost')) byId('fuel-total-cost').textContent = formatCurrency(0, currency);
    if (byId('fuel-per-person')) byId('fuel-per-person').textContent = formatCurrency(0, currency);
    if (byId('fuel-total-liters')) byId('fuel-total-liters').textContent = `0 ${isAr ? 'لتر' : 'L'}`;
    if (byId('fuel-tafgeet-val')) byId('fuel-tafgeet-val').textContent = '';
    return;
  }

  const res = calculateFuelTrip({
    distance,
    isRoundTrip,
    efficiencyUnit,
    efficiencyValue,
    fuelPrice,
    passengers
  });

  if (byId('fuel-total-cost')) byId('fuel-total-cost').textContent = formatCurrency(res.totalCost, currency);
  if (byId('fuel-per-person')) byId('fuel-per-person').textContent = formatCurrency(res.costPerPerson, currency);
  if (byId('fuel-total-liters')) byId('fuel-total-liters').textContent = `${formatNumber(res.litersNeeded)} ${isAr ? 'لتر' : 'L'}`;
  if (byId('fuel-cost-100km')) byId('fuel-cost-100km').textContent = formatCurrency(res.costPer100km, currency);
  if (byId('fuel-total-dist')) byId('fuel-total-dist').textContent = `${formatNumber(res.totalDistance)} ${isAr ? 'كم' : 'km'}`;

  const tafgeetEl = byId('fuel-tafgeet-val');
  if (tafgeetEl) {
    tafgeetEl.textContent = getInvoiceTafgeet(res.totalCost.toFixed(2), currency);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFuelCostCalculator);
  } else {
    initFuelCostCalculator();
  }
}
